import type { ProductCatalogItem } from '../../../../../types/product-catalog';
import type { ProductClientDetails } from '../../../../../types/product-detail';
import type { Obligation } from '../../../../../types/ztb';
import ProductDetailFaqSection from './faq-section';
import ProductDetailFeatureCards, { hasFeatureCards } from './feature-cards';
import ProductDetailHero from './hero';
import ProductDetailPricingTiers from './pricing-tiers';
import ProductDetailRecommendations from './recommendations';

type Props = {
  item: ProductCatalogItem;
  content: ProductClientDetails;
  obligations: Array<Obligation>;
  recommendedProductIds: Array<string>;
  onContactClick: (item: ProductCatalogItem) => void;
  onOrderInProgress: () => void;
};

const ProductDetailPage = ({
  item,
  content,
  obligations,
  recommendedProductIds,
  onContactClick,
  onOrderInProgress,
}: Props) => {
  // askAviPrompts belongs to whichever featureSections entry actually renders first — an empty
  // leading section (hasFeatureCards false) would otherwise silently swallow it, since
  // ProductDetailFeatureCards returns null for that entry and never renders its own prompts block.
  // findIndex, not array index 0, is what "first" means here.
  const firstRenderableFeatureSectionIndex = content.featureSections.findIndex(hasFeatureCards);

  return (
    <div data-ref='buy.product-detail.page'>
      <ProductDetailHero
        item={item}
        breadcrumbLabel={content.breadcrumbLabel}
        breadcrumbNavItems={content.breadcrumbNavItems}
        heroTitle={content.heroTitle}
        heroImageUrl={content.heroImageUrl}
        heroPricing={content.heroPricing}
        hasPricing={content.pricingTiers.items.length > 0}
        isEligibleForOnlinePurchase={content.isEligibleForOnlinePurchase}
        onContactClick={() => onContactClick(item)}
        onOrderInProgress={onOrderInProgress}
      />
      {/* Hero-pricing products (e.g. Registrations) put price + CTAs directly in the hero rather
        than this section — their Figma design has no breathing room between the hero and the
        section right after it, unlike the rest of this page's sections, so pad-top-none
        overrides pad-all-xl's own top padding for that case only. */}
      <div
        data-ref='buy.product-detail.sections'
        className={['pad-all-xl', content.heroPricing && 'pad-top-none'].filter(Boolean).join(' ')}
      >
        {content.featureSections.map((section, index) => (
          <ProductDetailFeatureCards
            key={section.heading ?? index}
            features={section}
            sectionIndex={index}
            tightTopSpacing={index === 0 && !!content.heroPricing}
            askAviPrompts={index === firstRenderableFeatureSectionIndex ? content.askAviPrompts : undefined}
          />
        ))}
        <ProductDetailPricingTiers
          pricingTiers={content.pricingTiers}
          isEligibleForOnlinePurchase={content.isEligibleForOnlinePurchase}
          onContactClick={() => onContactClick(item)}
          onOrderInProgress={onOrderInProgress}
        />
        <ProductDetailFaqSection faqs={content.faqs} />
        <ProductDetailRecommendations
          currentProductId={item.id}
          recommendedProductIds={recommendedProductIds}
          obligations={obligations}
          onContactClick={onContactClick}
        />
      </div>
    </div>
  );
};

export default ProductDetailPage;
